/**
 * useStores — the Store Ops store scope: the estate list + the active store
 * driving the TOPBAR store switcher and every store-scoped screen (faithful
 * to the 3-in-1's top-bar store dropdown).
 *
 * Cross-tenant leak fix, round 2 (docket 2026-07-27, CD-DOCKET-OVERNIGHT item
 * 1 — the round-1 fix from 2026-07-26 was incomplete): round 1 added a
 * tenant-scoped storage key + a purge-and-refetch-on-change handler + a
 * request token, but (a) the module-global singleton was still a single slot
 * keyed by whatever `_tenantSlug` happened to be at boot (often unresolved —
 * `null` maps to a SHARED `__so_active_store:none` bucket), and (b) the purge
 * handler fired SYNCHRONOUSLY inside `switchWorkspace` BEFORE the real
 * session-switch op resolved, so `ensureFetched()` could issue a request that
 * — against a real backend — still runs under the OUTGOING tenant's session
 * while being labelled with the INCOMING tenant's slug. Fixed now by:
 *  (a) never fetching until `getConfirmedTenantSlug()` is non-null (never the
 *      optimistic identity) — purge on `onTenantSwitching` (immediate, no
 *      fetch), (re)fetch only on `onTenantChange` (confirmed);
 *  (b) a generation check (`getTenantGeneration()`) alongside the existing
 *      per-request token, so a late response can never overwrite a newer
 *      tenant's state;
 *  (c) no fictional default store id — "bond" (a specific tenant's real store)
 *      was hardcoded as the fallback selection; ACTIVE now starts unresolved
 *      (`""`) and `useActiveStore()`'s existing `stores[0]` fallback takes
 *      over once the confirmed tenant's real stores load.
 */
import { useEffect, useState } from "react";
import { listStores, type SoStore } from "./so-api";
import { can, getConfirmedTenantSlug, getTenantGeneration, onTenantChange, onTenantSwitching } from "../../shell/access";

const keyFor = (slug: string | null) => `__so_active_store:${slug ?? "none"}`;

let _tenantSlug: string | null = null; // set only once a tenant is CONFIRMED
let STORES: SoStore[] = [];
let ACTIVE = ""; // "" = unresolved — never a fictional/tenant-specific default
let _fetched = false;
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());

function ensureFetched() {
  if (_fetched) return;
  // Capability gate (docket 2026-07-31, persona-inappropriate prefetch): the
  // topbar's StoreSwitcherSlot calls useStores() + useActiveStore() at the top
  // of its body for EVERY persona — its can("storeops.read")/can("mybook.read")
  // check only gates the RENDER, after both hooks (and this fetch) have already
  // run, which is also why /v1/admin/storeops/stores fired twice. React forbids
  // a conditional hook, so the switcher can't move the calls behind its check;
  // this is the one place that can stop the request. Same gate the rail and
  // ROUTE_READ_PERM already apply to every storeops.* route.
  if (!can("storeops.read") && !can("mybook.read")) return;
  const slug = getConfirmedTenantSlug();
  if (!slug) return; // nothing confirmed yet (boot, or a switch still pending) — onTenantChange will retry
  _fetched = true;
  const gen = getTenantGeneration();
  listStores()
    .then((r) => {
      if (gen !== getTenantGeneration() || slug !== _tenantSlug) return; // superseded by a newer tenant — discard, never let it overwrite the new tenant's state
      STORES = Array.isArray(r?.stores) ? r.stores : [];
      if (!ACTIVE || !STORES.some((s) => s.id === ACTIVE)) ACTIVE = STORES[0]?.id ?? "";
      notify();
    })
    .catch((e) => { if (gen === getTenantGeneration()) { console.warn("listStores failed:", e); _fetched = false; } });
}

export function setActiveStore(id: string): void {
  ACTIVE = id;
  try { sessionStorage.setItem(keyFor(_tenantSlug), id); } catch { /* ok */ }
  notify();
}

// Purge SYNCHRONOUSLY the instant a switch is optimistically initiated —
// never show tenant A's stores under tenant B's chrome — but do NOT fetch
// yet (see the file header: a fetch issued here would still run under
// tenant A's session against a real backend).
onTenantSwitching(() => {
  STORES = [];
  ACTIVE = "";
  _fetched = false;
  notify();
});
// Fetch only once the tenant is CONFIRMED — initial resolution, a completed
// switch, or a rollback to the previous tenant after a failed switch.
onTenantChange(() => {
  _tenantSlug = getConfirmedTenantSlug();
  _fetched = false;
  try { ACTIVE = (_tenantSlug && sessionStorage.getItem(keyFor(_tenantSlug))) || ""; } catch { ACTIVE = ""; }
  notify();
  ensureFetched();
});

export function useStores(): SoStore[] {
  const [, force] = useState(0);
  useEffect(() => { ensureFetched(); const f = () => force((n) => n + 1); subs.add(f); return () => { subs.delete(f); }; }, []);
  return STORES;
}

export function useActiveStore(): { storeId: string; store: SoStore | null } {
  const stores = useStores();
  const [, force] = useState(0);
  useEffect(() => { const f = () => force((n) => n + 1); subs.add(f); return () => { subs.delete(f); }; }, []);
  return { storeId: ACTIVE, store: stores.find((s) => s.id === ACTIVE) ?? stores[0] ?? null };
}

export function attainColor(v: number): string {
  return v >= 100 ? "var(--jrni-color-semantic-green-1)" : v >= 85 ? "#B08D57" : "var(--jrni-color-semantic-orange-1)";
}
